import { accountApi, adminApi } from '@/api/client';
import type { AuthUser } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Alert, App as AntdApp, Button, Card, Form, Input, Tabs, Typography } from 'antd';
import type { AxiosError } from 'axios';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const { Title, Paragraph } = Typography;

/**
 * 登录页（[S8]）—— 自研统一身份（account-service OIDC IdP），不经 sub2api。
 *
 * 流程（授权码 + PKCE，页内一次性完成，无需真实跳转）：
 *  1. 生成 code_verifier / code_challenge(S256)。
 *  2. POST /account-api/login（邮箱+密码+authorize 参数）→ 设会话 + 返回 redirect_to(含 code)。
 *  3. POST /account-api/token（code + verifier）→ account access token(RS256)。
 *  4. 写入 AuthContext（token → 当前标签页 sessionStorage）；adminApi 拦截器据此注入
 *     Authorization: Bearer；admin core 用 JWKS 本地验签认出运维授权。
 *  5. 回跳来源页。
 */
interface LocationState {
  from?: { pathname?: string };
}

interface LoginFormValues {
  email: string;
  password: string;
}

interface LegacyLoginValues {
  username: string;
  password: string;
  totp?: string;
}

interface LegacyAdmin {
  id: number;
  username: string | null;
  email: string | null;
  role: string;
}

const OIDC_CLIENT_ID = 'admin-console';
const OIDC_SCOPE = 'openid profile email';
/** 注册在 account-service 的 redirect_uri，必须与 client 完全一致（页内换码校验用）。 */
function redirectUri(): string {
  return `${window.location.origin}/sso`;
}

/** base64url(无填充) */
function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier(): string {
  const a = new Uint8Array(32);
  crypto.getRandomValues(a);
  return b64url(a);
}

async function challengeOf(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

/** 从 account access token 的 payload 提取 sub / email，构造 AuthUser。 */
function userFromToken(token: string, fallbackEmail: string): AuthUser {
  try {
    const raw = token.split('.')[1] ?? '';
    const json = JSON.parse(atob(raw.replace(/-/g, '+').replace(/_/g, '/')));
    return {
      id: String(json.sub ?? fallbackEmail),
      email: typeof json.email === 'string' ? json.email : fallbackEmail,
      roles: Array.isArray(json.roles) && json.roles.length ? json.roles.map(String) : undefined,
    };
  } catch {
    return { id: fallbackEmail, email: fallbackEmail };
  }
}

interface LoginRaw {
  redirect_to?: string;
  location?: string;
}
interface TokenRaw {
  access_token?: string;
  expires_in?: number;
}

export default function LoginPage() {
  const { login, loginWithSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = AntdApp.useApp();
  const [loading, setLoading] = useState(false);

  const from = (location.state as LocationState | null)?.from?.pathname ?? '/';

  const onFinish = async (values: LoginFormValues) => {
    setLoading(true);
    try {
      const email = values.email.trim();
      const verifier = randomVerifier();
      const challenge = await challengeOf(verifier);
      const ruri = redirectUri();

      // 1) 登录 + 取授权码
      const loginRes = await accountApi.post<LoginRaw>('/login', {
        email,
        password: values.password,
        response_type: 'code',
        client_id: OIDC_CLIENT_ID,
        redirect_uri: ruri,
        scope: OIDC_SCOPE,
        code_challenge: challenge,
        code_challenge_method: 'S256',
      });
      const loc = loginRes.data?.redirect_to ?? loginRes.data?.location ?? '';
      const code = new URL(loc, window.location.origin).searchParams.get('code');
      if (!code) {
        message.error('登录失败：未获取到授权码');
        return;
      }

      // 2) 换 access token
      const tokRes = await accountApi.post<TokenRaw>('/token', {
        grant_type: 'authorization_code',
        code,
        code_verifier: verifier,
        client_id: OIDC_CLIENT_ID,
        redirect_uri: ruri,
      });
      const token = tokRes.data?.access_token;
      if (!token) {
        message.error('登录失败：服务未返回访问令牌');
        return;
      }

      login(token, userFromToken(token, email));
      message.success('登录成功');
      navigate(from, { replace: true });
    } catch (err) {
      const ax = err as AxiosError<{
        message?: string;
        error?: { message?: string; details?: { retry_after_seconds?: number } };
      }>;
      const status = ax.response?.status;
      const serverMsg = ax.response?.data?.error?.message ?? ax.response?.data?.message;
      if (status === 401 || status === 403) {
        message.error('邮箱或密码错误，或该账号无运维权限');
      } else if (status === 429) {
        const wait = ax.response?.data?.error?.details?.retry_after_seconds ?? 60;
        message.error(`登录操作过于频繁，请 ${wait} 秒后重试`);
      } else if (serverMsg) {
        message.error(serverMsg);
      } else if (ax.code === 'ERR_NETWORK') {
        message.error('无法连接登录服务，请稍后重试');
      } else {
        message.error('登录失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const onLegacyFinish = async (values: LegacyLoginValues) => {
    setLoading(true);
    try {
      const res = await adminApi.post<LegacyAdmin>('/auth/login', {
        username: values.username.trim(),
        password: values.password,
        ...(values.totp ? { totp: values.totp.trim() } : {}),
      });
      loginWithSession({
        id: String(res.data.id),
        email: res.data.email ?? undefined,
        name: res.data.username ?? undefined,
        roles: [res.data.role],
      });
      message.success('后台专用账号登录成功');
      navigate(from, { replace: true });
    } catch (err) {
      const ax = err as AxiosError<{
        error?: { message?: string; details?: { mfa_required?: boolean } };
      }>;
      if (ax.response?.data?.error?.details?.mfa_required) {
        message.warning('该账号已启用 MFA，请填写动态验证码后重试');
      } else {
        message.error('后台专用账号或密码不正确');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <Card className="admin-login__panel" variant="borderless">
        <div className="admin-login__brand">
          <div className="admin-login__mark">D</div>
          <Title level={2} style={{ marginBottom: 8 }}>
            欢迎回到 DramaClaw
          </Title>
          <Paragraph className="admin-login__caption" style={{ marginBottom: 0 }}>
            登录运营工作台，管理内容生产、AI 资源与平台服务。
          </Paragraph>
        </div>
        <Tabs
          style={{ marginTop: 12 }}
          items={[
            {
              key: 'oidc',
              label: '统一账号',
              children: (
                <Form<LoginFormValues>
                  layout="vertical"
                  onFinish={onFinish}
                  requiredMark={false}
                  disabled={loading}
                >
                  <Form.Item
                    name="email"
                    label="邮箱"
                    rules={[
                      { required: true, message: '请输入邮箱' },
                      { type: 'email', message: '邮箱格式不正确' },
                    ]}
                  >
                    <Input
                      prefix={<MailOutlined />}
                      placeholder="you@example.com"
                      autoComplete="username"
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label="密码"
                    rules={[{ required: true, message: '请输入密码' }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="密码"
                      autoComplete="current-password"
                      size="large"
                    />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                    登录
                  </Button>
                </Form>
              ),
            },
            {
              key: 'legacy',
              label: '后台专用账号',
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message="财务人员及独立运维人员从这里登录；该账号不能登录用户端。"
                    style={{ marginBottom: 12 }}
                  />
                  <Form<LegacyLoginValues>
                    layout="vertical"
                    onFinish={onLegacyFinish}
                    requiredMark={false}
                    disabled={loading}
                  >
                    <Form.Item
                      name="username"
                      label="后台用户名"
                      rules={[{ required: true, message: '请输入用户名' }]}
                    >
                      <Input prefix={<MailOutlined />} autoComplete="username" size="large" />
                    </Form.Item>
                    <Form.Item
                      name="password"
                      label="密码"
                      rules={[{ required: true, message: '请输入密码' }]}
                    >
                      <Input.Password
                        prefix={<LockOutlined />}
                        autoComplete="current-password"
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item name="totp" label="动态验证码（已启用 MFA 时）">
                      <Input inputMode="numeric" maxLength={6} size="large" />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                      登录管理后台
                    </Button>
                  </Form>
                </>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
